import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { EmpButton as Button, EmpSpace as Space, EmpTag as Tag, TileCard } from '@empowernow/ui';
import PageHeading from '../core/a11y/PageHeading';
import { workflowApi } from '../core/services/api';
import { subscribe } from '../core/services/sse';
import ExecutionView from '../widgets/ExecutionView';

export default function WorkflowRunner() {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [status, setStatus] = useState<any | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const waitingMeta = (status && (status.waiting || status.result?.waiting || status.data?.result?.waiting)) || null;
  const stateVersion = waitingMeta?.state_version || waitingMeta?.request_format?.headers?.['If-Match'] || null;
  const idempotencyKey = waitingMeta?.idempotency_key || waitingMeta?.request_format?.body?.idempotency_key || null;

  const { data: wf, isLoading: loadingWf, error: wfError } = useQuery({
    queryKey: ['workflow', workflowId],
    queryFn: () => workflowApi.getWorkflow(workflowId!),
    enabled: !!workflowId,
  });

  const start = useMutation({
    mutationFn: () => workflowApi.executeById(workflowId!),
    onSuccess: (res: any) => {
      const id = res?.data?.result?.workflow?.id || res?.data?.execution_id || res?.data?.correlation_id;
      setExecutionId(id || null);
    }
  });

  const cancel = useMutation({
    mutationFn: () => workflowApi.cancel(executionId!),
  });

  // Attach SSE when we have an execution id
  useEffect(() => {
    if (!executionId) return;
    unsubscribeRef.current?.();
    unsubscribeRef.current = subscribe(`/api/crud/events/workflow/status/${executionId}`,
      (evt: any) => setStatus(evt));
    return () => { unsubscribeRef.current?.(); };
  }, [executionId]);

  if (loadingWf) return <div className="glass-card p-4" role="status" aria-live="polite">Loading workflow…</div>;
  if (wfError || !wf) return (
    <TileCard theme="auto" elevation={1}>
      <div style={{ padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>Failed to load workflow</div>
        <Button onClick={() => navigate('/workflows')}>Back to Workflows</Button>
      </div>
    </TileCard>
  );

  const name = (wf as any)?.data?.name ?? (wf as any)?.name;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <PageHeading title={name || 'Workflow'}>{name || 'Workflow'}</PageHeading>
        <Button onClick={() => navigate('/workflows')}>Back to Workflows</Button>
      </div>

      <TileCard theme="auto" elevation={1}>
        <div style={{ padding: 12 }}>
          <Space>
            <Button type="primary" loading={start.isPending} onClick={() => start.mutate()}>Start</Button>
            {executionId && <Tag color="blue">Execution: {executionId}</Tag>}
            {status?.status && <Tag color={status.status === 'COMPLETED' ? 'green' : status.status === 'FAILED' ? 'red' : 'geekblue'}>{status.status}</Tag>}
            {stateVersion && <Tag color="gold">state_version: {stateVersion}</Tag>}
            {idempotencyKey && <Tag color="purple">idempotency_key: {idempotencyKey}</Tag>}
            {executionId && status?.status === 'RUNNING' && (
              <Button danger loading={cancel.isPending} onClick={() => cancel.mutate()}>Cancel</Button>
            )}
          </Space>
        </div>
      </TileCard>

      {status?.result && (
        <TileCard theme="auto" elevation={1}>
          <div style={{ padding: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Result</div>
            <ExecutionView execution={status} />
          </div>
        </TileCard>
      )}
    </Space>
  );
}


